<template>
  <v-dialog
    v-model="show"
    max-width="500px"
    >
      <v-card>
        <v-card-title>
          Request For Absence
          <v-spacer></v-spacer>
          <v-btn
            icon
            blur
            @click.prevent="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <!-- Request for -->
            <v-col cols="12" md="6">
              <v-select
                hide-details
                v-model="requestFor"
                :items="requestForItems"
                label="Request For"
                @change="toDate = undefined; toTime = undefined; fromTime = undefined"
              >
              </v-select>
            </v-col>
            <!-- /Request for -->
            <!-- Employee List -->
            <v-col cols="12" md="6">
              <v-select
                hide-details
                v-model="notificationData.Notification_Employee_ID_To"
                :items="employeeList"
                label="Request To"
                item-text="full_name"
                item-value="employee_id"
              >
                <template v-slot:prepend-item>
                  <v-container>
                    <v-row>
                      <v-col>
                        <v-text-field
                          dense
                          hide-details
                          label="Search employee"
                          v-model="searchEmployee"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </template>
              </v-select>
            </v-col>
            <!-- /Employee List -->
          </v-row>
          <v-row v-if="requestFor === 'Sick leave'">
            <v-col cols="12">
              <v-textarea
                v-model="sickLeaveReason"
                label="Illness Reason"
                :rules="[rules.required, rules.englishOnly]"
                auto-grow
                rows="2"
                :counter="500"
                :maxlength="500"
                @input="validateSickLeaveReason"
              />
            </v-col>
          </v-row>
          <v-row v-if="requestFor">
            <!-- Date From -->
            <v-col cols="12" md="6">
              <DatePicker
                v-model="fromDate"
                label="Start Date"
                :max="toDate"
                :allowedDates="allowedDates"
              />
            </v-col>
            <!-- /Date From -->
            <!-- Date To -->
            <v-col cols="12" md="6" v-if="showEndDate">
              <DatePicker
                v-model="toDate"
                label="End Date"
                :min="fromDate"
                :allowedDates="allowedDates"
              />
            </v-col>
            <!-- /Date To -->
          </v-row>
          <v-row v-if="showTimepickers">
            <!-- Time from -->
            <v-col cols="12" md="6">
              <v-menu
                ref="menuTimeFrom"
                v-model="menuTimeFrom"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="fromTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="fromTime"
                    label="Time From"
                    prepend-icon="mdi-clock-time-four-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  format="24hr"
                  min="10:00"
                  :max="maxHours"
                  :allowed-minutes="allowedMinutes"
                  v-if="menuTimeFrom"
                  v-model="fromTime"
                  full-width
                  @click:minute="$refs.menuTimeFrom.save(fromTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <!-- /Time from -->
            <!-- Time to -->
            <v-col cols="12" md="6" v-if="showEndTimepicker">
              <v-menu
                ref="menuTimeTo"
                v-model="menuTimeTo"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="toTime"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="toTime"
                    label="Time To"
                    prepend-icon="mdi-clock-time-four-outline"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  format="24hr"
                  :min="fromTime || '10:00'"
                  max="19:00"
                  :allowed-minutes="allowedMinutes"
                  v-if="menuTimeTo"
                  v-model="toTime"
                  full-width
                  @click:minute="$refs.menuTimeTo.save(toTime)"
                ></v-time-picker>
              </v-menu>
            </v-col>
            <!-- /Time to -->
          </v-row>
          <v-row v-if="requestFor === 'Sick leave'">
            <v-col cols="12" md="6" lg="6">
              <v-file-input
                label="Confirmation File"
                v-model="confirmationFile"
                @change="onConfirmationCheckFileChange"
                show-size
                counter
                accept=".doc, .docx, application/pdf, .jpg, application/msword, image/jpg"
              ></v-file-input>
            </v-col>
            <v-col cols="12" md="6" lg="6">
              <v-select
                v-model="selectedAmountSickDays"
                label="Selected Days"
                :items="getAvailableAmountSickDays"
              >
                <template v-slot:prepend>
                  <v-tooltip bottom max-width="300px" color="#424852">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        color="primary"
                        style="cursor:pointer;"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    If you want to take sick leave for less than <b>{{ getAvailableAmountSickDays[0] }}</b>
                    {{ periodString }}, you can
                    change the number of selected {{ periodString }} to
                    <b>{{ getAvailableAmountSickDays[1] }}</b>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small :loading="isLoading" :disabled="!isRequestReady || !isRequestAppropriate || isLoading" color="primary" @click="sendRequest">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>
<script>
import moment from 'moment'

export default {
  name: 'AbsenceRequest',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    value: {
      required: true,
      default: false
    }
  },
  data () {
    return {
      showCancelDialog: false,
      isRequestAppropriate: true,
      isProductionEnv: process.env.NODE_ENV === 'production',
      menuTimeFrom: false,
      menuTimeTo: false,
      notificationsTypes: [],
      notificationData: {
        Notification_Type_ID: 1,
        Notification_Title: '',
        Notification_Message: '',
        Notification_Created: undefined,
        Notification_Employee_ID_From: undefined,
        Notification_Employee_ID_To: undefined
      },
      requestFor: undefined,
      requestForItems: ['Vacation', 'Half day vacation', 'Step out', 'Sick leave'],
      fromDate: undefined,
      fromTime: undefined,
      toDate: undefined,
      toTime: undefined,
      searchEmployee: '',
      selectedAmountSickDays: undefined,
      sickLeaveReason: '',
      confirmationFileName: '',
      confirmationFileType: '',
      confirmationFile: null,
      isLoading: false,
      hasCyrillic: false,
      rules: {
        required: value => !!(value && value.toString().trim().length) || 'Reason is required',
        englishOnly: value => {
          if (!value) return true
          const hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(value)
          return !hasCyrillic || 'English only'
        }
      }
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        if (!value) {
          this.clearAll()
          this.isRequestAppropriate = true
        }
      }
    },
    employeeList () {
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return this.$store.getters.getEmployee
          .filter(item => this.isProductionEnv ? item.employee_id !== this.user.employee_id : item)
          .filter(item => {
            reg.lastIndex = 0
            return reg.test(item.full_name.toLowerCase())
          })
      } else return this.$store.getters.getEmployee.filter(item => this.isProductionEnv ? item.employee_id !== this.user.employee_id : item)
    },
    getAvailableAmountSickDays () {
      if (this.fromDate && this.toDate) {
        const fromDate = moment(this.fromDate)
        const toDate = moment(this.toDate)
        const diffInDays = toDate.diff(fromDate, 'days')
        let businessDaysDiff = 0

        for (let i = 0; i <= diffInDays; i++) {
          const currentDay = fromDate.clone().add(i, 'days')

          if (this.allowedDates(currentDay)) {
            businessDaysDiff++
          }
        }
        return [businessDaysDiff, businessDaysDiff - 0.5]
      } else {
        return []
      }
    },
    periodString () {
      return this.selectedAmountSickDays <= 1 ? 'day' : 'days'
    },
    maxHours () {
      return this.requestFor === 'Half day vacation' ? '15:00' : '19:00'
    },
    showEndDate () {
      return this.requestFor && ['Vacation', 'Sick leave'].indexOf(this.requestFor) > -1
    },
    showEndTimepicker () {
      return this.fromTime && this.requestFor && this.requestFor !== 'Half day vacation'
    },
    showTimepickers () {
      return this.requestFor && ['Vacation', 'Sick leave'].indexOf(this.requestFor) === -1
    },
    hasSickLeaveReason () {
      return !!(this.sickLeaveReason &&
                this.sickLeaveReason.toString().trim().length &&
                !this.hasCyrillic)
    },
    sickLeaveReasonText () {
      return this.sickLeaveReason ? this.sickLeaveReason.toString().trim() : ''
    },
    user () {
      return this.$store.getters.getUser
    },
    notifications () {
      return this.$store.getters.getNotifications
    },
    isRequestReady () {
      let condition = false
      if (this.requestFor === 'Vacation' || this.requestFor === 'Sick leave') {
        condition = this.fromDate && this.toDate
        if (this.requestFor === 'Sick leave') {
          condition = condition && this.hasSickLeaveReason
        }
      }
      if (this.requestFor === 'Half day vacation') {
        condition = this.fromDate && this.fromTime
      }
      if (this.requestFor === 'Step out') {
        condition = this.fromDate && this.fromTime && this.toTime
      }
      return condition && this.notificationData.Notification_Employee_ID_To
    }
  },
  mounted () {
    this.getEmployeeList()
  },
  watch: {
    getAvailableAmountSickDays: {
      handler (newVal) {
        if (newVal.length > 0) {
          this.selectedAmountSickDays = newVal[0]
        }
      }
    }
  },
  methods: {
    sendRequest () {
      this.isLoading = true
      this.isRequestAppropriate = false

      if (this.requestFor === 'Vacation') {
        this.notificationData.Notification_Message =
          `${this.user.full_name} wants to take ${this.requestFor.toLowerCase()} from ${this.fromDate} to ${this.toDate}`
      }
      if (this.requestFor === 'Sick leave') {
        this.notificationData.Notification_Message =
          `${this.user.full_name} wants to take ${this.requestFor.toLowerCase()} from ${this.fromDate} to ${this.toDate} for ${this.selectedAmountSickDays} ${this.periodString}. Reason: ${this.sickLeaveReasonText}`
      }
      if (this.requestFor === 'Half day vacation' || this.requestFor === 'Step out') {
        const from = new Date(`${this.fromDate} ${this.fromTime}`)
        const to = new Date(from.getTime() + (4 * 60 * 60 * 1000))
        this.toTime = this.toTime || `${to.getHours().toString().padStart(2, '0')}:${to.getMinutes().toString().padStart(2, '0')}`
        this.notificationData.Notification_Message =
          `${this.user.full_name} wants to take ${this.requestFor.toLowerCase()} from ${this.fromTime} to ${this.toTime} on ${this.fromDate}`
      }
      this.notificationData.Notification_Employee_ID_From = this.user.employee_id
      this.notificationData.Notification_Title = `Request for ${this.requestFor.toLowerCase()}`
      this.notificationData.Notification_Created = new Date().toISOString()

      this.axios.post('/api/notifications', this.notificationData, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.notificationData.Notification_ID = response.data[0].Notification_ID
        return this.postAbsenceRequest(response.data[0].Notification_ID)
      }).then(response => {
        if (this.requestFor === 'Sick leave') {
          this.sendEmailForSickLeaveRequest(response.data[0].Request_ID)
        }
        this.show = false
        this.$store.dispatch('setSuccess', { successMsg: 'Request successfully sent' })
      }).catch(error => {
        this.isRequestAppropriate = true
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      }).finally(() => {
        this.isLoading = false
      })
    },
    clearAll () {
      this.requestFor = undefined
      this.fromDate = undefined
      this.fromTime = undefined
      this.toDate = undefined
      this.toTime = undefined
      this.searchEmployee = ''
      this.selectedAmountSickDays = undefined
      this.sickLeaveReason = ''
      this.hasCyrillic = false
      this.notificationData.Notification_Employee_ID_To = undefined
      this.confirmationFileName = ''
      this.confirmationFileType = ''
      this.confirmationFile = null
    },
    allowedDates (date) {
      const dayOfTheWeek = moment(date).format('dd').toLowerCase()
      return ['sa', 'su'].indexOf(dayOfTheWeek) === -1
    },
    allowedMinutes (m) {
      return m === 30 || m === 0
    },
    getEmployeeList () {
      if (!this.employeeList.length) {
        this.axios.get('/api/employeelist', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
          this.$store.dispatch('setEmployeeList', { employeeList: response.data })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    postAbsenceRequest (notificationID) {
      let period = ''
      if (this.requestFor === 'Vacation') {
        period = `Period: from ${this.fromDate} to ${this.toDate}`
      }
      if (this.requestFor === 'Sick leave') {
        period = `Period: from ${this.fromDate} to ${this.toDate} for ${this.selectedAmountSickDays} ${this.periodString}`
      }
      if (this.requestFor === 'Half day vacation' || this.requestFor === 'Step out') {
        period = `Period: from ${this.fromTime} to ${this.toTime} on ${this.fromDate}`
      }
      const descriptionLines = [
        `Request for ${this.requestFor.toLowerCase()}`,
        period
      ]
      if (this.requestFor === 'Sick leave' && this.sickLeaveReasonText) {
        descriptionLines.push(`Reason: ${this.sickLeaveReasonText}`)
      }
      const requestData = {
        Description: descriptionLines.filter(Boolean).join('\n'),
        Creator_ID: this.notificationData.Notification_Employee_ID_From,
        Confirmer_ID: this.notificationData.Notification_Employee_ID_To,
        Confirmation_File_Name: this.confirmationFileName,
        Confirmation_File_Type: this.confirmationFileType,
        Confirmation_File: this.confirmationFile,
        Notification_ID: notificationID
      }
      if (this.requestFor === 'Sick leave') {
        requestData.Start_Date = this.fromDate ? this.fromDate : null
        requestData.End_Date = this.toDate ? this.toDate : null
        requestData.Duration = this.selectedAmountSickDays ? this.selectedAmountSickDays : null
      }
      return this.axios.post('/api/requests/absence', requestData,
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
    },
    validateSickLeaveReason () {
      if (this.sickLeaveReason) {
        this.hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(this.sickLeaveReason)
      } else {
        this.hasCyrillic = false
      }
    },
    onConfirmationCheckFileChange (e) {
      this.onFileChange(e, this.confirmationFile)
    },
    onFileChange (e, file) {
      if (e) {
        const reader = new FileReader()
        reader.onload = () => {
          file.data = reader.result
          let binary = ''
          const bytes = new Uint8Array(file.data)
          const len = bytes.byteLength
          for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i])
          }
          file.base64String = `data:${e.type};base64, ${window.btoa(binary)}`
        }
        this.confirmationFileName = e.name
        this.confirmationFileType = e.type
        reader.readAsArrayBuffer(e)
      }
    },
    sendEmailForSickLeaveRequest (requestId) {
      const body = {
        requestId: requestId
      }
      this.axios.post('/api/emails/sendemailforsickleaverequest', body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setSuccess', { successMsg: 'Email sent' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message })
      })
    }
  }
}
</script>
